import { Component } from '@angular/core';
import { NgClass } from '@angular/common';

interface AnalysisChip {
  label: string;
}

interface AnalysisRow {
  label: string;
  value: string;
  valueTone?: 'default' | 'success' | 'warning' | 'danger';
  valueBadge?: boolean;
}

interface AnalysisTab {
  id: string;
  label: string;
  headerTitle: string;
  contentTitle: string;
  chips?: AnalysisChip[];
  rows: AnalysisRow[];
  footer?: string;
}

@Component({
  selector: 'app-home-analysis-section',
  standalone: true,
  imports: [NgClass],
  templateUrl: './home-analysis-section.component.html'
})
export class HomeAnalysisSectionComponent {
  readonly tabs: AnalysisTab[] = [
    {
      id: 'informacoes-oficiais',
      label: 'Informações oficiais',
      headerTitle: 'Apartamento · São Paulo, SP',
      contentTitle: 'Informações oficiais do leilão',
      rows: [
        { label: 'Modalidade', value: 'Extrajudicial' },
        { label: '1º leilão', value: '13/07/2026 · Valor inicial: R$ 350.000,00' },
        { label: '2º leilão', value: '17/07/2026 · Valor inicial: R$ 221.208,04' },
        { label: 'Leiloeiro', value: 'Caixa', valueTone: 'default' },
        { label: 'Desconto sobre avaliação', value: '37%', valueTone: 'success' }
      ],
      footer: 'Edital, matrícula e link oficial do leiloeiro disponíveis no detalhe.'
    },
    {
      id: 'simulacao-custos',
      label: 'Simulação de custos',
      headerTitle: 'Apartamento · São Paulo, SP',
      contentTitle: 'Projeção de revenda estimada',
      rows: [
        { label: 'Valor bruto de venda estimado', value: 'R$ 450.000,00' },
        { label: 'Comissão corretagem (2%)', value: 'R$ 9.000,00' },
        { label: 'Outros custos de venda estimados', value: 'R$ 0,00' },
        { label: 'Tributos sobre a venda estimados', value: 'R$ 0,00' },
        { label: 'Venda líquida estimada', value: 'R$ 441.000,00', valueTone: 'success' }
      ],
      footer: 'Valores estimados para ajudar na análise inicial antes de avançar para o edital e a documentação.'
    },
    {
      id: 'analise-entorno',
      label: 'Análise do entorno',
      headerTitle: 'Apartamento · São Paulo, SP',
      contentTitle: 'Análise do entorno',
      chips: [
        { label: 'Metro a 600m' },
        { label: '5 escolas proximas' },
        { label: 'Mercado a 200m' },
        { label: 'Hospital a 1,2km' }
      ],
      rows: [
        { label: 'Valor médio do m2 no bairro', value: 'R$ 6.662' },
        { label: 'Valorização (12 meses)', value: '+7,4%', valueTone: 'success' },
        { label: 'Perfil da região', value: 'Residencial / misto' }
      ]
    },
    {
      id: 'validacao-riscos',
      label: 'Validação e riscos',
      headerTitle: 'Apartamento · São Paulo, SP',
      contentTitle: 'Validação e riscos',
      rows: [
        { label: 'Situação de ocupação', value: 'Ocupado', valueTone: 'warning', valueBadge: true },
        { label: 'Dívidas de condomínio / IPTU', value: 'Quitadas até o leilão', valueTone: 'success', valueBadge: true },
        { label: 'Situação processual', value: 'Sem pendências', valueTone: 'success', valueBadge: true },
        { label: 'Ações de terceiros', value: '1 alerta', valueTone: 'danger', valueBadge: true }
      ],
      footer: 'Cada risco vem com a fonte e a orientação do que verificar antes do lance.'
    }
  ];

  selectedTabIndex = 0;

  get selectedTab(): AnalysisTab {
    return this.tabs[this.selectedTabIndex];
  }

  selectTab(index: number): void {
    this.selectedTabIndex = index;
  }

  selectTabFromDropdown(value: string): void {
    const nextIndex = Number.parseInt(value, 10);

    if (Number.isNaN(nextIndex) || nextIndex < 0 || nextIndex >= this.tabs.length) {
      return;
    }

    this.selectTab(nextIndex);
  }

  onTabKeydown(event: KeyboardEvent, currentIndex: number): void {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') {
      return;
    }

    event.preventDefault();

    if (event.key === 'Home') {
      this.focusTab(0);
      return;
    }

    if (event.key === 'End') {
      this.focusTab(this.tabs.length - 1);
      return;
    }

    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
    this.focusTab(nextIndex);
  }

  getValueClasses(row: AnalysisRow): string {
    if (row.valueBadge) {
      switch (row.valueTone) {
        case 'success':
          return 'bg-green-100 text-green-700';
        case 'warning':
          return 'bg-amber-100 text-amber-700';
        case 'danger':
          return 'bg-red-100 text-red-700';
        default:
          return 'bg-gray-100 text-gray-700';
      }
    }

    switch (row.valueTone) {
      case 'success':
        return 'text-green-600';
      case 'warning':
        return 'text-amber-700';
      case 'danger':
        return 'text-red-600';
      default:
        return 'text-gray-900';
    }
  }

  private focusTab(index: number): void {
    this.selectedTabIndex = index;

    const tabElement = document.getElementById(`analysis-tab-${index}`);
    tabElement?.focus();
  }
}
